import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card, Col, Divider, Form, Image as AntdImage, InputNumber, Row, Select, Slider, Space, Typography, Upload, message } from 'antd';
import { InboxOutlined, DownloadOutlined, DeleteOutlined, CompressOutlined, SettingOutlined, FileImageOutlined } from '@ant-design/icons';
import Button from '../../components/common/Button';

type OutputFormat = 'image/jpeg' | 'image/webp' | 'same';

interface ImageInfo {
  width: number;
  height: number;
  sizeBytes: number;
}

const bytesToReadable = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const getImageInfo = async (file: File | Blob): Promise<ImageInfo> => {
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
  const img = new window.Image();
  img.src = dataUrl;
  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = reject;
  });
  return { width: img.naturalWidth, height: img.naturalHeight, sizeBytes: file.size };
};

const CorImageCompressor: React.FC = () => {
  const [form] = Form.useForm();

  const [originalFile, setOriginalFile] = useState<File | null>(null);
  const [originalPreview, setOriginalPreview] = useState<string | null>(null);
  const [originalInfo, setOriginalInfo] = useState<ImageInfo | null>(null);

  const [compressedBlob, setCompressedBlob] = useState<Blob | null>(null);
  const [compressedUrl, setCompressedUrl] = useState<string | null>(null);
  const [compressedInfo, setCompressedInfo] = useState<ImageInfo | null>(null);

  const objectUrlRef = useRef<string | null>(null);

  useEffect(() => {
    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, []);

  const resetAll = useCallback(() => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    setOriginalFile(null);
    setOriginalPreview(null);
    setOriginalInfo(null);
    setCompressedBlob(null);
    setCompressedUrl(null);
    setCompressedInfo(null);
  }, []);

  const readPreview = async (file: File) => {
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
    return dataUrl;
  };

  const onSelectFile = useCallback(async (file: File) => {
    try {
      setOriginalFile(file);
      setOriginalPreview(await readPreview(file));
      setOriginalInfo(await getImageInfo(file));
      setCompressedBlob(null);
      setCompressedUrl(null);
      setCompressedInfo(null);
      message.success('Görsel yüklendi');
    } catch (e) {
      message.error('Görsel okunamadı');
    }
  }, []);

  const compress = useCallback(async () => {
    if (!originalFile) {
      message.warning('Önce bir görsel seçin');
      return;
    }

    const { maxWidth, maxHeight, quality, format } = form.getFieldsValue();
    const dataUrl = await readPreview(originalFile);
    const img = new window.Image();
    img.src = dataUrl;
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = reject;
    });

    const srcW = img.naturalWidth;
    const srcH = img.naturalHeight;
    const ratio = Math.min(1, Math.min(maxWidth / srcW, maxHeight / srcH));
    const targetW = Math.max(1, Math.round(srcW * ratio));
    const targetH = Math.max(1, Math.round(srcH * ratio));

    const canvas = document.createElement('canvas');
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      message.error('Canvas başlatılamadı');
      return;
    }
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, 0, 0, targetW, targetH);

    const mime: OutputFormat = format === 'same' ? (originalFile.type as OutputFormat) : format;

    const blob: Blob = await new Promise((resolve, reject) => {
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error('Sıkıştırma başarısız'))),
        mime === 'same' ? originalFile.type : mime,
        Math.min(1, Math.max(0, quality))
      );
    });

    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    const url = URL.createObjectURL(blob);
    objectUrlRef.current = url;

    setCompressedBlob(blob);
    setCompressedUrl(url);
    setCompressedInfo(await getImageInfo(blob));
    message.success('Sıkıştırma tamamlandı');
  }, [form, originalFile]);

  useEffect(() => {
    form.setFieldsValue({ maxWidth: 1920, maxHeight: 1080, quality: 0.8, format: 'image/jpeg' as OutputFormat });
  }, [form]);

  const downloadName = useMemo(() => {
    if (!originalFile) return 'compressed-image';
    const base = originalFile.name.replace(/\.[^.]+$/, '');
    return `${base}-compressed`;
  }, [originalFile]);

  return (
    <div style={{ padding: 12 }}>
      <Space align="center" style={{ width: '100%', justifyContent: 'space-between', marginBottom: 8 }}>
        <Space>
          <CompressOutlined />
          <Typography.Title level={4} style={{ margin: 0 }}>Görsel Küçültücü</Typography.Title>
        </Space>
        <Space>
          <Button icon={<DeleteOutlined />} onClick={resetAll}>Temizle</Button>
          <Button type="primary" icon={<CompressOutlined />} onClick={compress}>Sıkıştır</Button>
        </Space>
      </Space>

      <Card title={<Space><SettingOutlined /> <span>Ayarlar</span></Space>} size="small" style={{ marginBottom: 12 }}>
        <Form layout="vertical" form={form}>
          <Row gutter={12}>
            <Col xs={24} sm={12} md={6}>
              <Form.Item label="Maks. Genişlik (px)" name="maxWidth" rules={[{ required: true }]}>
                <InputNumber style={{ width: '100%' }} min={1} max={10000} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item label="Maks. Yükseklik (px)" name="maxHeight" rules={[{ required: true }]}>
                <InputNumber style={{ width: '100%' }} min={1} max={10000} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item label={`Kalite`} name="quality" rules={[{ required: true }]}>
                <Slider min={0.1} max={1} step={0.05} tooltip={{ formatter: (v) => `${Math.round((v || 0) * 100)}%` }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item label="Çıktı Formatı" name="format" rules={[{ required: true }]}>
                <Select
                  options={[
                    { label: 'JPEG', value: 'image/jpeg' },
                    { label: 'WEBP', value: 'image/webp' },
                    { label: 'Orijinal', value: 'same' },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Card>

      <Row gutter={12}>
        <Col xs={24} md={12}>
          <Card
            size="small"
            title={<Space><FileImageOutlined /> <span>Orijinal</span></Space>}
            extra={originalInfo ? <span>{originalInfo.width}×{originalInfo.height} • {bytesToReadable(originalInfo.sizeBytes)}</span> : undefined}
          >
            <Upload.Dragger
              multiple={false}
              accept="image/*"
              beforeUpload={(file) => { onSelectFile(file); return false; }}
              showUploadList={false}
            >
              {originalPreview ? (
                <AntdImage src={originalPreview} alt="original" style={{ maxHeight: 360, objectFit: 'contain' }} />
              ) : (
                <div style={{ padding: 24 }}>
                  <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                  <p className="ant-upload-text">Görseli buraya sürükleyin ya da seçin</p>
                  <p className="ant-upload-hint">PNG, JPG, WEBP desteklenir</p>
                </div>
              )}
            </Upload.Dragger>
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card
            size="small"
            title={<Space><CompressOutlined /> <span>Sıkıştırılmış</span></Space>}
            extra={compressedInfo ? <span>{compressedInfo.width}×{compressedInfo.height} • {bytesToReadable(compressedInfo.sizeBytes)}</span> : undefined}
          >
            {compressedUrl ? (
              <>
                <AntdImage src={compressedUrl} alt="compressed" style={{ maxHeight: 360, objectFit: 'contain' }} />
                <Divider style={{ margin: '8px 0' }} />
                <Space>
                  <a href={compressedUrl} download={`${downloadName}.${(compressedBlob?.type || 'image/jpeg').split('/')[1]}`}>
                    <Button type="primary" icon={<DownloadOutlined />}>İndir</Button>
                  </a>
                  <Button icon={<CompressOutlined />} onClick={compress}>Tekrar Sıkıştır</Button>
                </Space>
              </>
            ) : (
              <Typography.Text type="secondary">Sıkıştırma sonucu burada görünecek</Typography.Text>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default CorImageCompressor;

